import React, { useEffect, useState } from 'react';
import { Box, Button, Alert, Chip } from '@mui/material';
import datetimeHelper from 'helpers/datetimeHelper';
import { PendingUpdateItem, State, useReviewUpdatesContext } from '../context';
import contentConversionHelper from '../../../helpers/contentConversionHelper';
import { getContentForDisplay } from '../../../helpers/contentDisplayHelper';
import { useManualTextEntities } from '../../../_data_sources/useManualTextEntities';
import HtmlDiff from 'htmldiff-js';
import { translate as t } from 'hooks/useTranslations';
import ContentPanel from './ContentPanel';
import EditablePanel from './EditablePanel';
import _ from 'lodash';

const ReviewSection: React.FC = () => {
  const {
    focus,
    manual,
    isLoading,
    policyUpdates,
    prefaceUpdates,
    sidebarItems,
    handleAcceptRecommendations,
    handleRetainCurrent,
    handleSaveCustomizations,
  } = useReviewUpdatesContext();
  const { textEntities } = useManualTextEntities(manual?.id);
  const section = (policyUpdates?.sections || []).find((x) => x.id === focus);
  let initialContent: string = section?.currentContent || '';
  let initialTitle: string = section?.currentTitle || '';
  let updateCreatedAt: string = section?.changeDate || '';
  let lastUpdatedAt: string = section?.updatedDate || '';
  let titleNumber: string = section?.sectionNumber || '';
  if (policyUpdates) {
    if (policyUpdates.policyTitleUpdate && focus === 'policy-title') {
      initialTitle = policyUpdates.currentTitle;
      titleNumber = policyUpdates.policyNumber;
      updateCreatedAt = policyUpdates.changeDate;
      lastUpdatedAt = policyUpdates.updatedDate || '';
    } else if (section) {
      updateCreatedAt = section?.changeDate || '';
      lastUpdatedAt = section?.updatedDate || '';
    }
  }
  if (prefaceUpdates) {
    initialTitle = prefaceUpdates.currentTitle;
    initialContent = prefaceUpdates.currentContent;
    updateCreatedAt = prefaceUpdates.changeDate;
    lastUpdatedAt = prefaceUpdates.updatedDate || '';
  }

  const [editorContent, setEditorContent] = useState(initialContent);
  const [editorTitle, setEditorTitle] = useState(initialTitle);
  const [rejectCustomize, setRejectCustomize] = useState(false);

  useEffect(() => {
    if (focus) {
      setEditorContent(initialContent);
      setEditorTitle(initialTitle);
      setRejectCustomize(false);
    }
  }, [focus, initialContent, initialTitle]);

  const formattedCurrentDate = lastUpdatedAt
    ? datetimeHelper.formatDateTime(lastUpdatedAt, {
        format: 'monthDayYearTime',
        missingDisplay: '',
      })
    : '';
  const formattedChangeDate = updateCreatedAt
    ? datetimeHelper.formatDateTime(updateCreatedAt, {
        format: 'monthDayYearTime',
        missingDisplay: '',
      })
    : '';

  let currentContent: string = section?.currentContent || '';
  let updatedContent: string = section?.updatedContent || '';
  if (prefaceUpdates) {
    currentContent = prefaceUpdates.currentContent;
    updatedContent = prefaceUpdates.updatedContent;
  }

  const diffHTMLContent = HtmlDiff.execute(
    getContentForDisplay(currentContent, textEntities),
    getContentForDisplay(updatedContent, textEntities)
  );

  let currentTitle: string = section?.currentTitle || '';
  let updatedTitle: string = section?.updatedTitle || '';
  if (policyUpdates && policyUpdates.policyTitleUpdate && focus === 'policy-title') {
    currentTitle = policyUpdates.currentTitle;
    updatedTitle = policyUpdates.updatedTitle;
  }
  if (prefaceUpdates && prefaceUpdates.prefaceTitleUpdate) {
    currentTitle = prefaceUpdates.currentTitle;
    updatedTitle = prefaceUpdates.updatedTitle;
  }

  const diffHTMLTitle = HtmlDiff.execute(
    getContentForDisplay(currentTitle, textEntities),
    getContentForDisplay(updatedTitle, textEntities)
  );

  // Check if the only change is an edit level tag change
  const isEditLevelOnlyChange =
    section?.sectionEditLevelUpdate &&
    !section?.sectionTitleUpdate &&
    !section?.sectionContentUpdate &&
    !section?.stateChange &&
    !section?.isNew;

  const item = sidebarItems.find((x) => x.id === focus);
  const itemIndex = _.indexOf(sidebarItems, item);
  return rejectCustomize ? (
    <Box
      id={`customize_${focus}`}
      sx={{ marginInline: 6, marginBottom: 4, borderRadius: '6px' }}
    >
      <Box sx={{ display: 'flex', gap: 4 }}>
        <EditablePanel
          id={item?.id || ''}
          index={itemIndex || ''}
          title={editorTitle}
          content={editorContent || ''}
          label={t(
            manual?.manualLevel === 'AGENCY'
              ? 'draftManuals.reviewUpdates.agencyContent'
              : 'draftManuals.reviewUpdates.currentContent'
          )}
          titleNumber={titleNumber}
          date={formattedCurrentDate}
          editLevelTag={section?.currentEditLevelTag}
          onTitleChange={(e) => setEditorTitle(e.target.value)}
          onContentSave={(newContent) => setEditorContent(newContent)}
          isPolicyEdit={focus === 'policy-title'}
        />
        <ContentPanel
          index={itemIndex || ''}
          title={diffHTMLTitle || ''}
          titleNumber={titleNumber}
          content={diffHTMLContent}
          label={t(
            manual?.manualLevel === 'AGENCY'
              ? 'draftManuals.reviewUpdates.lexipolUpdate'
              : 'draftManuals.reviewUpdates.updatedContent'
          )}
          date={formattedChangeDate + t('draftManuals.reviewUpdates.current')}
          additionalStyles={{ width: '50%' }}
          isPolicyEdit={focus === 'policy-title'}
          manualId={manual?.id || ''}
          attachments={section?.updatedAttachments || []}
          editLevelTag={section?.updatedEditLevelTag}
          isEditLevelChanged={section?.sectionEditLevelUpdate}
        />
      </Box>
      <Box sx={{ display: 'flex', width: '50%', justifyContent: 'end', gap: 2, pr: 4, pt: 4, mb: 4 }}>
        <Button
          variant='outlined'
          size='small'
          disabled={isLoading}
          onClick={() => setRejectCustomize(false)}
        >
          {t('common.cancel')}
        </Button>
        <Button
          variant='outlined'
          size='small'
          disabled={isLoading}
          onClick={() => handleRetainCurrent(focus || '')}
        >
          {t('draftManuals.reviewUpdates.retainCurrent')}
        </Button>
        <Button
          size='small'
          disabled={isLoading}
          onClick={() => handleSaveCustomizations(focus || '', editorContent || '', editorTitle)}
        >
          {t('draftManuals.reviewUpdates.saveCustomizations')}
        </Button>
      </Box>
    </Box>
  ) : (
    <Box
      id={`change_${focus}`}
      sx={{ marginInline: 6, marginBottom: 4 }}
    >
      {isEditLevelOnlyChange && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Alert severity='warning'>
            {t('draftManuals.reviewUpdates.editLevelTagUpdated', '', {
              type: section?.isSubsection ? 'Subsection' : 'Section',
            })}
          </Alert>
        </Box>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: section?.isNew ? '1fr' : '1fr 1fr', gap: 4 }}>
        {(!section?.isNew || rejectCustomize) && (
          <Box
            sx={{
              position: 'relative',
              pt: section?.stateChange && section.updatedState === 'DISABLED' ? 14 : 0,
              pb: 2,
            }}
          >
            <ContentPanel
              index={itemIndex || ''}
              title={editorTitle}
              titleNumber={titleNumber}
              content={editorContent || ''}
              label={t(
                manual?.manualLevel === 'AGENCY'
                  ? 'draftManuals.reviewUpdates.agencyContent'
                  : 'draftManuals.reviewUpdates.currentContent'
              )}
              date={formattedCurrentDate}
              isPolicyEdit={focus === 'policy-title'}
              manualId={manual?.id || ''}
              attachments={section?.currentAttachments || []}
              editLevelTag={section?.currentEditLevelTag}
            />
          </Box>
        )}
        {section?.stateChange && section.updatedState === 'DISABLED' ? (
          <Box sx={{ position: 'relative', pt: 14, pb: 2 }}>
            <Alert
              severity='info'
              sx={{ position: 'absolute', bottom: 'calc(100% - 50px)', left: 0, right: 0, zIndex: 10 }}
            >
              {t('draftManuals.reviewUpdates.lexipolHasDisabledContent')}
            </Alert>
            <ContentPanel
              index={itemIndex || ''}
              title={diffHTMLTitle || ''}
              titleNumber={titleNumber}
              content={diffHTMLContent}
              label={t(
                manual?.manualLevel === 'AGENCY'
                  ? 'draftManuals.reviewUpdates.lexipolUpdate'
                  : 'draftManuals.reviewUpdates.updatedContent'
              )}
              date={formattedChangeDate + t('draftManuals.reviewUpdates.current')}
              additionalStyles={{ opacity: 0.3 }}
              isPolicyEdit={focus === 'policy-title'}
              manualId={manual?.id || ''}
              attachments={section?.updatedAttachments || []}
              editLevelTag={section?.updatedEditLevelTag}
              isEditLevelChanged={section?.sectionEditLevelUpdate}
            />
          </Box>
        ) : (
          <Box sx={{ position: 'relative' }}>
            {section?.isNew && (
              <Chip
                label={t('draftManuals.toc.new')}
                color='primary'
                sx={{ position: 'sticky', top: 0, zIndex: 1, mb: 2 }}
              />
            )}
            <ContentPanel
              index={itemIndex || ''}
              title={section?.isNew ? currentTitle : diffHTMLTitle || ''}
              titleNumber={titleNumber}
              content={section?.isNew ? currentContent : diffHTMLContent}
              label={t(
                manual?.manualLevel === 'AGENCY'
                  ? 'draftManuals.reviewUpdates.lexipolUpdate'
                  : 'draftManuals.reviewUpdates.updatedContent'
              )}
              date={
                section?.isNew ? formattedCurrentDate : formattedChangeDate + t('draftManuals.reviewUpdates.current')
              }
              additionalStyles={{}}
              isPolicyEdit={focus === 'policy-title'}
              manualId={manual?.id || ''}
              attachments={section?.isNew ? section?.currentAttachments || [] : section?.updatedAttachments || []}
              editLevelTag={section?.isNew ? section?.currentEditLevelTag : section?.updatedEditLevelTag}
              isEditLevelChanged={section?.isNew ? false : section?.sectionEditLevelUpdate}
            />
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'end', gap: 2, mt: 4, mb: 4 }}>
        {isEditLevelOnlyChange ? (
          <Button
            size='small'
            disabled={isLoading}
            onClick={() => handleAcceptRecommendations(focus || '')}
          >
            {t('common.ok')}
          </Button>
        ) : (
          <>
            <Button
              variant='outlined'
              size='small'
              disabled={isLoading}
              onClick={() => setRejectCustomize(true)}
            >
              {t('draftManuals.reviewUpdates.reject&Customize')}
            </Button>
            <Button
              size='small'
              disabled={isLoading}
              onClick={() => handleAcceptRecommendations(focus || '')}
            >
              {t('draftManuals.reviewUpdates.acceptRecommendations')}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default ReviewSection;
